"use client";

import Link from "next/link";
import Image from "next/image";
import { Newspaper } from "lucide-react";
import type { BlogPostMeta } from "@/lib/blog-utils";
import { useT } from "@/hooks/useT";
import { displayTraderName } from "@/app/activity/helpers/format";

interface TraderRelatedArticlesProps {
  articles: BlogPostMeta[];
  username: string | null;
}

export default function TraderRelatedArticles({ articles, username }: TraderRelatedArticlesProps) {
  const t = useT();
  if (articles.length === 0) return null;

  // Wallet addresses (0x… 42 chars) get truncated to "0x2a2C…9Bc1" so
  // the heading doesn't read "News about @0x2a2C53bD278c04DA9962Fcf…"
  // when the trader has no real username. displayTraderName passes real
  // usernames / ENS / labels through unchanged.
  const displayName = username ? `@${displayTraderName(username)}` : t("traders.articles.thisTrader");

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden transition-colors duration-300">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 pt-4 pb-3">
        <Newspaper className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {t("traders.articles.title", { name: displayName })}
        </h3>
      </div>

      {/* Articles */}
      <div className="px-3 pb-3 space-y-1">
        {articles.map((article) => (
          <Link
            key={article.slug}
            href={`/blog/${article.slug}`}
            className="flex gap-3 p-2 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors group"
          >
            {/* Cover image */}
            {article.cover && (
              <div className="w-24 h-[50px] rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                <Image
                  src={article.cover}
                  alt={article.title}
                  width={96}
                  height={50}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Text */}
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                {article.title}
              </h4>
              <p className="text-[11px] text-gray-400 mt-1">
                {new Date(article.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
